import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { API, graphqlOperation } from 'aws-amplify';
import {
  SESSION_KEY_STATE,
  SESSION_KEY_CODE_VERIFIER,
  validateOAuthState,
  cleanupSessionStorage,
  buildTokenExchangeBody,
} from './utils/oauthFlow';
import { getEagleViewConfig } from './utils/eagleviewConfig';

const storeEagleViewTokenMutation = /* GraphQL */ `
  mutation StoreEagleViewToken(
    $userID: String!
    $accessToken: String!
    $refreshToken: String!
    $expiresIn: Int!
  ) {
    storeEagleViewToken(
      userID: $userID
      accessToken: $accessToken
      refreshToken: $refreshToken
      expiresIn: $expiresIn
    ) {
      success
      message
    }
  }
`;

/**
 * EagleViewCallback component — handles the OAuth redirect from EagleView.
 *
 * On mount:
 * 1. Parses `code`, `state`, and `error` from URL query parameters
 * 2. If `error` param is present: displays error description, cleans up sessionStorage
 * 3. Validates `state` matches the value stored in sessionStorage
 * 4. If state mismatch: displays "Security validation failed" error, cleans up, stops
 * 5. On successful validation: performs token exchange with authorization code
 * 6. On token exchange success: calls StoreEagleViewToken mutation
 * 7. On mutation success: displays success message and redirects to setup page
 */
export const EagleViewCallback: React.FC = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>(
    'processing'
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Parse URL query parameters
      const params = new URLSearchParams(window.location.search);
      const error = params.get('error');
      const errorDescription = params.get('error_description');
      const code = params.get('code');
      const state = params.get('state');

      // If error param is present, display error and clean up
      if (error) {
        const message = errorDescription || error || 'Authorization failed';
        setErrorMessage(message);
        setStatus('error');
        cleanupSessionStorage();
        return;
      }

      // Validate state parameter against stored value
      const storedState = sessionStorage.getItem(SESSION_KEY_STATE);

      if (!state || !validateOAuthState(state, storedState)) {
        setErrorMessage('Security validation failed');
        setStatus('error');
        cleanupSessionStorage();
        return;
      }

      // Ensure we have an authorization code
      if (!code) {
        setErrorMessage('No authorization code received');
        setStatus('error');
        cleanupSessionStorage();
        return;
      }

      // Retrieve code verifier from sessionStorage
      const codeVerifier = sessionStorage.getItem(SESSION_KEY_CODE_VERIFIER);
      if (!codeVerifier) {
        setErrorMessage(
          'Code verifier not found. Please try connecting again.'
        );
        setStatus('error');
        cleanupSessionStorage();
        return;
      }

      // Get OAuth configuration
      const config = getEagleViewConfig();

      // Perform token exchange
      const tokenBody = buildTokenExchangeBody(
        code,
        config.redirectUri,
        config.clientId,
        config.clientSecret,
        codeVerifier
      );

      const tokenResponse = await fetch(config.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: tokenBody,
      });

      if (!tokenResponse.ok) {
        let errorDetail = `Token exchange failed (${tokenResponse.status})`;
        try {
          const errorBody = await tokenResponse.json();
          if (errorBody.error_description) {
            errorDetail = errorBody.error_description;
          } else if (errorBody.error) {
            errorDetail = errorBody.error;
          }
        } catch {
          // If we can't parse the error body, use the status-based message
        }
        setErrorMessage(errorDetail);
        setStatus('error');
        cleanupSessionStorage();
        return;
      }

      const tokenData = await tokenResponse.json();

      // Get user ID from global context
      const me = (window as any).me;
      if (!me?.userID) {
        setErrorMessage('User information not available');
        setStatus('error');
        cleanupSessionStorage();
        return;
      }

      // Call StoreEagleViewToken mutation — tokens go directly to backend,
      // never stored in browser storage
      const mutationResponse: any = await API.graphql(
        graphqlOperation(storeEagleViewTokenMutation, {
          userID: me.userID,
          accessToken: tokenData.access_token,
          refreshToken: tokenData.refresh_token || '',
          expiresIn: tokenData.expires_in,
        })
      );

      const result = mutationResponse?.data?.storeEagleViewToken;
      if (!result?.success) {
        setErrorMessage(result?.message || 'Could not store token');
        setStatus('error');
        cleanupSessionStorage();
        return;
      }

      // Success — display message and redirect after brief delay
      setStatus('success');
      cleanupSessionStorage();

      setTimeout(() => {
        window.location.href = '/settings/integrations/eagleview/setup';
      }, 2000);
    } catch (err: any) {
      const message =
        err?.errors?.[0]?.message ||
        err?.message ||
        'An unexpected error occurred during authorization';
      setErrorMessage(message);
      setStatus('error');
      cleanupSessionStorage();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      {status === 'processing' && (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">Connecting to EagleView...</Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we complete the authorization.
          </Typography>
        </Box>
      )}

      {status === 'error' && (
        <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Please try connecting again from the EagleView setup page.
          </Typography>
        </Box>
      )}

      {status === 'success' && (
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully connected to EagleView!
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Redirecting to setup page...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EagleViewCallback;
